import { memo, useMemo, useState } from 'react';

import { ModalCloseButton } from '@air/primitive-modal';
import { type CurrentPlanPricing, type PlanPricing } from '@air/types';

import { UpgradePlanModalBillingCycleToggle } from '~/components/Modals/PlansModal/UpgradePlanModalBillingCycleToggle';
import { UpgradePlanModalPlanCard } from '~/components/Modals/PlansModal/UpgradePlanModalPlanCard';
import { UpgradePlanModalSidebar } from '~/components/Modals/PlansModal/UpgradePlanModalSidebar';
import {
  getAvailablePlanInfo,
  isUpgradePlanModalBillingCycle,
  type UpgradePlanModalBillingCycle,
} from '~/components/Modals/PlansModal/utils';
import { type EnhancedSubscription } from '~/providers/SubscriptionProvider';

/**
 * @TODO(AI-993): This V2 branch/version of the plan upgrade modal will be baselined following
 * the release of credit-based pricing.
 */

export type UpgradePlanModalContentProps = {
  plans: (PlanPricing | CurrentPlanPricing)[];
  subscription: EnhancedSubscription;
  onClose: () => void;
};

export const UpgradePlanModalContent = memo(({ plans, subscription, onClose }: UpgradePlanModalContentProps) => {
  const { currentPlan, availablePlans } = useMemo(
    () => getAvailablePlanInfo({ plans, subscription }),
    [plans, subscription],
  );

  const [billingCycle, setBillingCycle] = useState<UpgradePlanModalBillingCycle>(
    isUpgradePlanModalBillingCycle(subscription.billingCycle) ? subscription.billingCycle : 'annual',
  );

  return (
    <div className="relative flex flex-col bg-gray-3 sm:rounded-8 lg:flex-row">
      <ModalCloseButton onClick={onClose} className="absolute top-2 right-2" size="small" />
      <UpgradePlanModalSidebar currentPlan={currentPlan} billingCycle={billingCycle} />
      <div className="flex w-full flex-col">
        <div className="flex w-full justify-center px-8 py-4 lg:pb-8">
          <UpgradePlanModalBillingCycleToggle billingCycle={billingCycle} onBillingCycleChange={setBillingCycle} />
        </div>
        <div className="flex h-full flex-col items-center justify-center gap-4 px-8 pb-8 lg:flex-row lg:items-stretch">
          {availablePlans.map((plan) => (
            <UpgradePlanModalPlanCard
              key={plan.planId}
              {...plan}
              currentPlan={currentPlan}
              billingCycle={billingCycle}
              onClose={onClose}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

UpgradePlanModalContent.displayName = 'UpgradePlanModalContent';
